import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { ActionTitleActions } from "@/components/actionTitle/ActionTitleActions";
import { useActiveProject } from "@/features/project/ActiveProject";
import { RECOMMENDED_ACTION_IDS } from "@/features/project/projectAI/utils";
import { ListView, ListViewItem, createNewIcon } from "@repo/skapa";
import { plusIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { PaddedContainer, useCustomer, useSettings } from "@repo/ui";
import { Line } from "@repo/ui";
import { SectionContainer } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { hasItems } from "@repo/utils";
import { useState } from "react";

import { useSearchCustomerOrders } from "../hooks/searchOrder";
import { handleCreateOrder } from "../utils";
import { Orders } from "./Orders";
import { AddOrderModal } from "./addOrder/AddOrderModal";
import { getNonDraftOrdersIds } from "./utils";

export const OrdersSection = ({ editable }: { editable: boolean }) => {
  const { market, language } = useSettings();
  const { opportunity } = useActiveProject();

  const { customer, isValidating } = useCustomer(opportunity?.id);

  const { data: customerOrders } = useSearchCustomerOrders(
    isValidating,
    opportunity?.customerType,
    customer?.email,
    opportunity?.partyId
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterAssociated, setFilterAssociated] = useState<boolean>(false);

  const orderIds = getNonDraftOrdersIds(opportunity);
  const ordersWithoutAppointments = customerOrders?.filter(
    (order) => order.clientSystem !== "BOKA"
  );

  const unassociatedOrder = ordersWithoutAppointments?.filter((res) => {
    return !orderIds.includes(res?.orderNo);
  });

  return (
    <PaddedContainer id={RECOMMENDED_ACTION_IDS.ORDERS_SECTION}>
      <SectionContainer>
        <ActionTitle
          title={`${
            orderIds.length > 1 ? t("label.orders") : t("label.order")
          } (${orderIds.length})`}
          action={
            editable && (
              <ActionTitleActions
                actions={[
                  {
                    id: "create-order-action",
                    icon: createNewIcon,
                    onClick: () => {
                      if (opportunity) {
                        handleCreateOrder(opportunity, market, language);
                        trackAnalytics("create_order");
                      }
                    },
                    text: t("action.create_order"),
                    title: t("action.create_order"),
                    iconOnly: true,
                  },
                  {
                    id: "add-order-action",
                    icon: plusIcon,
                    onClick: () => {
                      setIsOpen(true);
                      trackAnalytics("add_order");
                    },
                    text: t("action.add_order"),
                    title: t("action.add_order"),
                    iconOnly: true,
                  },
                ]}
              />
            )
          }
        />
        <Orders editable={editable} />
      </SectionContainer>

      {hasItems(unassociatedOrder) && (
        <ListView id="disassociated-order-list" size="small">
          <Line />
          <ListViewItem
            control={editable ? "navigational" : "default"}
            onClick={() => {
              setFilterAssociated(true);
              setIsOpen(true);
              trackAnalytics("add_disassociated_order");
            }}
            title={
              unassociatedOrder.length > 1
                ? t("prompt.found_multiple_orders_not_associated", {
                    total: unassociatedOrder.length,
                  })
                : t("prompt.found_one_order_not_associated")
            }
          />
        </ListView>
      )}
      {isOpen && (
        <AddOrderModal
          isOpen={isOpen}
          closeModal={() => {
            setIsOpen(false);
            setFilterAssociated(false);
          }}
          associatedOrders={orderIds}
          filterAssociated={filterAssociated}
        />
      )}
    </PaddedContainer>
  );
};
